import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackLead } from '../../lib/analytics';
import { useInquiry } from '../../hooks/useInquiry';
import { useAudience } from '../../contexts/AudienceContext';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

// T041 [US2]: Form state preservation - form data is stored in separate state from i18n translations
// This ensures that when language changes, only labels/messages update while user input is preserved
const CLIENT_TYPE_OPTIONS = [
  { value: 'individual', label: 'individual' },
  { value: 'student', label: 'student' },
  { value: 'organization', label: 'organization' },
];

interface ContactFormProps {
  preselectedServiceId?: string;
}

interface FormData {
  name: string;
  email: string;
  client_type: 'individual' | 'student' | 'organization';
  project_description: string;
  service_id?: string | undefined;
}

interface FormErrors {
  name?: string;
  email?: string;
  client_type?: string;
  project_description?: string;
  service_id?: string;
}

export function ContactForm({ preselectedServiceId }: ContactFormProps) {
  const { t, i18n } = useTranslation('contact');
  const navigate = useNavigate();
  const mutation = useInquiry();
  const { selectedAudience } = useAudience();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    client_type: selectedAudience || 'individual',
    project_description: '',
    service_id: preselectedServiceId,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>({
    name: false,
    email: false,
    client_type: false,
    project_description: false,
    service_id: false,
  });

  // Update service_id when preselectedServiceId changes
  useEffect(() => {
    if (preselectedServiceId) {
      setFormData((prev) => ({ ...prev, service_id: preselectedServiceId }));
    }
  }, [preselectedServiceId]);

  // Update client_type when selectedAudience changes
  useEffect(() => {
    if (selectedAudience) {
      setFormData((prev) => ({ ...prev, client_type: selectedAudience }));
    }
  }, [selectedAudience]);

  const validateField = (name: keyof FormData, value: string | undefined): string | undefined => {
    switch (name) {
      case 'name':
        if (!value?.trim()) return t('validation.nameRequired');
        if (value.length > 255) return t('validation.nameTooLong');
        return undefined;
      case 'email':
        if (!value?.trim()) return t('validation.emailRequired');
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) return t('validation.emailInvalid');
        return undefined;
      case 'project_description':
        if (!value?.trim()) return t('validation.descriptionRequired');
        if (value.length > 5000) return t('validation.descriptionTooLong');
        return undefined;
      case 'client_type':
        if (!value) return t('validation.clientTypeRequired');
        return undefined;
      case 'service_id':
        // service_id is optional, no validation needed
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name as keyof FormData]) {
      const error = validateField(name as keyof FormData, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (name: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, String(formData[name] ?? ''));
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const newErrors: FormErrors = {};
    let hasErrors = false;

    (Object.keys(formData) as Array<keyof FormData>).forEach((key) => {
      const error = validateField(key, String(formData[key] || ''));
      if (error) {
        newErrors[key] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      client_type: true,
      project_description: true,
      service_id: true,
    });

    if (hasErrors) return;

    try {
      await mutation.mutateAsync(formData);
      trackLead({
        audience: formData.client_type,
        language: i18n.language,
      });
    } catch {
      // Error is handled by the error state below
    }
  };

  if (mutation.isSuccess) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neon-green/20 mb-6">
          <CheckCircle className="w-8 h-8 text-neon-green" />
        </div>
        <h3 className="text-2xl font-bold text-text-primary mb-2 font-heading">{t('success.title')}</h3>
        <p className="text-text-secondary mb-6">
          {t('success.message')}
        </p>
        <Button onClick={() => navigate('/')}>{t('btn.back')}</Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      name="contact-form"
      role="form"
    >
      <Input
        label={t('form.name')}
        name="name"
        value={formData.name}
        onChange={handleChange}
        onBlur={() => handleBlur('name')}
        error={touched.name ? errors.name : undefined}
        placeholder={t('form.namePlaceholder')}
        required
      />

      <Input
        label={t('form.email')}
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        onBlur={() => handleBlur('email')}
        error={touched.email ? errors.email : undefined}
        placeholder={t('form.emailPlaceholder')}
        required
      />

      <Select
        label={t('form.clientType')}
        name="client_type"
        value={formData.client_type}
        onChange={handleChange}
        onBlur={() => handleBlur('client_type')}
        error={touched.client_type ? errors.client_type : undefined}
        options={CLIENT_TYPE_OPTIONS.map((opt) => ({
          value: opt.value,
          label: t(`clientType.${opt.value}`),
        }))}
        required
      />

      <Textarea
        label={t('form.projectDescription')}
        name="project_description"
        value={formData.project_description}
        onChange={handleChange}
        onBlur={() => handleBlur('project_description')}
        error={touched.project_description ? errors.project_description : undefined}
        placeholder={t('form.projectDescriptionPlaceholder')}
        rows={6}
        required
      />

      {mutation.isError && (
        <div className="flex items-center gap-2 p-4 glass-card rounded-xl border border-red-500/30">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400">
            {t('error.submitFailed')}
          </p>
        </div>
      )}

      <Button
        type="submit"
        disabled={mutation.isPending}
        className="w-full"
      >
        {mutation.isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('form.submitting')}
          </>
        ) : (
          t('form.submit')
        )}
      </Button>
    </form>
  );
}
